import { Alert, Button, Empty, Spin, Tag, Typography } from "@arco-design/web-react";

import { formatNotificationTime } from "../service";
import type { NotificationMessage } from "../types";
import "./NotificationCenter.css";

interface NotificationMessageListProps {
  items: NotificationMessage[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpenNotice: (message: NotificationMessage) => void;
  onOpenAction: (message: NotificationMessage) => void;
  compact?: boolean;
}

/** 渲染普通通知和 URL 消息共用的可访问消息列表。 */
export function NotificationMessageList({
  items,
  loading,
  error,
  onRetry,
  onOpenNotice,
  onOpenAction,
  compact = false,
}: NotificationMessageListProps) {
  if (loading) {
    return <div className="notification-message-list__state"><Spin tip="正在加载消息" /></div>;
  }
  if (error) {
    return (
      <div className="notification-message-list__state">
        <Alert
          type="error"
          showIcon
          content="消息加载失败，请稍后重试。"
          action={<Button size="mini" onClick={onRetry}>重新加载</Button>}
        />
      </div>
    );
  }
  if (items.length === 0) {
    return <div className="notification-message-list__state"><Empty description="暂无消息" /></div>;
  }

  return (
    <div className="notification-message-list" role="list" aria-label="消息列表">
      {items.map((message) => {
        const activate = () => {
          if (message.messageKind === "notice") onOpenNotice(message);
          else onOpenAction(message);
        };
        return (
          <article className="notification-message-item" role="listitem" key={message.id}>
            <div className="notification-message-item__topline">
              <button type="button" className="notification-message-item__title" onClick={activate}>
                {message.title}
              </button>
              <MessageStateTag message={message} />
            </div>
            {message.summary ? <p className="notification-message-item__summary">{message.summary}</p> : null}
            <div className="notification-message-item__meta">
              {message.actorNameSnapshot ? <span>{message.actorNameSnapshot}</span> : null}
              <time dateTime={message.occurredAt}>{formatNotificationTime(message.occurredAt)}</time>
            </div>
            {!compact && message.messageKind === "action_url" && !message.isClosed ? (
              <div className="notification-message-item__footer">
                <Typography.Text type="secondary">业务完成后由对应功能关闭</Typography.Text>
                <Button type="text" size="mini" onClick={activate}>
                  {message.actionLabel || "去处理"}
                </Button>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

/** 展示消息当前的阅读与处理状态。 */
function MessageStateTag({ message }: { message: NotificationMessage }) {
  if (message.isClosed) return <Tag color="gray">已关闭</Tag>;
  if (!message.isRead) return <Tag color="red">未读</Tag>;
  if (message.messageKind === "action_url") return <Tag color="orange">待处理</Tag>;
  return <Tag color="blue">已读</Tag>;
}
